"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDirectVectorTileUrl = useDirectVectorTileUrl;
const lodash_1 = __importDefault(require("lodash"));
const expressions_1 = require("@mwater/expressions");
const BlocksLayoutManager_1 = __importDefault(require("../layouts/blocks/BlocksLayoutManager"));
const WidgetFactory_1 = __importDefault(require("../widgets/WidgetFactory"));
const LayerFactory_1 = __importDefault(require("./LayerFactory"));
const MapBoundsCalculator_1 = __importDefault(require("./MapBoundsCalculator"));
const DirectWidgetDataSource_1 = __importDefault(require("../widgets/DirectWidgetDataSource"));
const compressJson_1 = __importDefault(require("../compressJson"));
const querystring_1 = __importDefault(require("querystring"));
const QuickfilterUtils = __importStar(require("../quickfilter/QuickfilterUtils"));
const react_1 = require("react");
const useStableCallback_1 = require("@mwater/react-library/lib/useStableCallback");
class DirectMapDataSource {
    options;
    // Create map url source that uses direct jsonql maps
    constructor(options) {
        this.options = options;
    }
    // Gets the data source for a layer
    getLayerDataSource(layerId) {
        // Get layerView
        const layerView = lodash_1.default.findWhere(this.options.design.layerViews, { id: layerId });
        if (!layerView) {
            throw new Error(`Layer ${layerId} not found`);
        }
        return new DirectLayerDataSource({ ...this.options, layerView });
    }
    // Gets the bounds for the map. Null for whole world. Callback as { n:, s:, w:, e: }
    getBounds(design, filters, callback) {
        return new MapBoundsCalculator_1.default(this.options.schema, this.options.dataSource).getBounds(design, filters, callback);
    }
    /** Gets the quickfilters data source */
    getQuickfiltersDataSource() {
        return {
            getValues: (index, expr, filters, offset, limit, callback) => {
                // Perform query
                return QuickfilterUtils.findExprValues(expr, this.options.schema, this.options.dataSource, filters, offset, limit, callback);
            }
        };
    }
}
exports.default = DirectMapDataSource;
/**
 * Fetches the URL for a vector tile source with an expiry time. For direct token access.
 */
async function getVectorTileFromDirectRequest(options) {
    const { apiUrl, client, directTokenRequest } = options;
    const response = await fetch(apiUrl + "vector_tiles/create_token/direct?client=" + (client || ""), {
        method: "POST",
        body: JSON.stringify(directTokenRequest),
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
        }
    });
    if (!response.ok) {
        throw new Error("Error getting tiles");
    }
    const { token, expires } = await response.json();
    // Client isn't necessary, but allows tracking of usage easier
    return { url: apiUrl + `vector_tiles/tiles/{z}/{x}/{y}?token=${token}&client=${client ?? ""}`, expires };
}
/**
 * React hook to get a direct URL for a vector tile source.
 * The URL is fetched asynchronously and the hook returns the current URL.
 * The URL is refreshed automatically when it expires.
 * @param options The options for fetching the vector tile source.
 * @returns The URL of the vector tile source, or null if it has not been fetched yet.
 */
function useDirectVectorTileUrl(options) {
    const { apiUrl, client, directTokenRequest } = options;
    const [url, setUrl] = (0, react_1.useState)(null);
    const [expires, setExpires] = (0, react_1.useState)(null);
    const fetchUrl = (0, useStableCallback_1.useStableCallback)(async () => {
        const { url, expires } = await getVectorTileFromDirectRequest({
            apiUrl,
            client,
            directTokenRequest,
        });
        setUrl(url);
        setExpires(expires);
    });
    (0, react_1.useEffect)(() => {
        fetchUrl();
    }, [apiUrl, client, JSON.stringify(directTokenRequest)]);
    // Refresh when expires
    (0, react_1.useEffect)(() => {
        if (expires) {
            const expiryDate = new Date(expires).getTime();
            const now = new Date().getTime();
            const delay = expiryDate - now;
            const timer = setTimeout(fetchUrl, delay);
            return () => clearTimeout(timer);
        }
        return;
    }, [expires]);
    return url;
}
class DirectLayerDataSource {
    options;
    // Create map url source that uses direct jsonql maps
    // options:
    //   schema: schema to use
    //   dataSource: general data source
    //   layerView: layerView to display
    //   apiUrl: API url to use for talking to mWater server
    //   client: client id to use for talking to mWater server
    constructor(options) {
        this.options = options;
    }
    /** Get the url for vector tile source with an expiry time. Only for layers of type "VectorTile"
     * @param createdAfter ISO 8601 timestamp requiring that tile soruce on server is created after specified datetime
     */
    async getVectorTileUrl(layerDesign, filters, createdAfter) {
        // Create layer
        const layer = LayerFactory_1.default.createLayer(this.options.layerView.type);
        // Put in opacity of 1 as it doesn't affect source data
        const vectorTile = layer.getVectorTile(layerDesign, this.options.layerView.id, this.options.schema, filters, 1);
        const directTokenRequest = {
            layers: vectorTile.sourceLayers,
            ctes: vectorTile.ctes,
            minZoom: vectorTile.minZoom,
            maxZoom: vectorTile.maxZoom,
            createdAfter: createdAfter,
            // 12 hours
            expiresAfter: new Date(Date.now() + 1000 * 3600 * 12).toISOString(),
            origin: this.options.origin
        };
        const { url, expires } = await getVectorTileFromDirectRequest({
            apiUrl: this.options.apiUrl,
            client: this.options.client,
            directTokenRequest
        });
        return { url, expires };
    }
    // Get the url for the image tiles with the specified filters applied
    // Called with (design, filters) where design is the design of the layer and filters are filters to apply. Returns URL
    getTileUrl(design, filters) {
        // Create layer
        const layer = LayerFactory_1.default.createLayer(this.options.layerView.type);
        // Handle special cases
        if (this.options.layerView.type === "MWaterServer") {
            return this.createLegacyUrl(design, "png", filters);
        }
        // If layer has tiles url directly available
        if (layer.getLayerDefinitionType() === "TileUrl") {
            return layer.getTileUrl(design, filters);
        }
        // Get JsonQLCss
        const jsonqlCss = layer.getJsonQLCss(design, this.options.schema, filters);
        return this.createUrl("png", jsonqlCss);
    }
    // Get the url for the interactivity tiles with the specified filters applied
    // Called with (design, filters) where design is the design of the layer and filters are filters to apply. Returns URL
    getUtfGridUrl(design, filters) {
        // Create layer
        const layer = LayerFactory_1.default.createLayer(this.options.layerView.type);
        // Handle special cases
        if (this.options.layerView.type === "MWaterServer") {
            return this.createLegacyUrl(design, "grid.json", filters);
        }
        // If layer has tiles url directly available
        if (layer.getLayerDefinitionType() === "TileUrl") {
            return layer.getUtfGridUrl(design, filters);
        }
        // Get JsonQLCss
        const jsonqlCss = layer.getJsonQLCss(design, this.options.schema, filters);
        return this.createUrl("grid.json", jsonqlCss);
    }
    // Gets widget data source for a popup widget
    getPopupWidgetDataSource(design, widgetId) {
        // Create layer
        let type;
        const layer = LayerFactory_1.default.createLayer(this.options.layerView.type);
        ({ type, design } = new BlocksLayoutManager_1.default().getWidgetTypeAndDesign(design.popup.items, widgetId));
        // Create widget
        const widget = WidgetFactory_1.default.createWidget(type);
        return new DirectWidgetDataSource_1.default({
            widget,
            schema: this.options.schema,
            dataSource: this.options.dataSource,
            apiUrl: this.options.apiUrl,
            client: this.options.client
        });
    }
    // Create query string
    createUrl(extension, jsonqlCss) {
        const query = {
            type: "jsonql",
            design: (0, compressJson_1.default)(jsonqlCss)
        };
        if (this.options.client) {
            query.client = this.options.client;
        }
        // Make URL change when cache expired
        const cacheExpiry = this.options.dataSource.getCacheExpiry();
        if (cacheExpiry) {
            query.cacheExpiry = cacheExpiry;
        }
        let url = `${this.options.apiUrl}maps/tiles/{z}/{x}/{y}.${extension}?` + querystring_1.default.stringify(query);
        // Add subdomains: {s} will be substituted with "a", "b" or "c" in leaflet for api.mwater.co only.
        // Used to speed queries
        if (url.match(/^https:\/\/api\.mwater\.co\//)) {
            url = url.replace(/^https:\/\/api\.mwater\.co\//, "https://{s}-api.mwater.co/");
        }
        return url;
    }
    // Create query string
    createLegacyUrl(design, extension, filters) {
        let where;
        let url = `${this.options.apiUrl}maps/tiles/{z}/{x}/{y}.${extension}?type=${design.type}&radius=1000`;
        // Add subdomains: {s} will be substituted with "a", "b" or "c" in leaflet for api.mwater.co only.
        // Used to speed queries
        if (url.match(/^https:\/\/api\.mwater\.co\//)) {
            url = url.replace(/^https:\/\/api\.mwater\.co\//, "https://{s}-api.mwater.co/");
        }
        if (this.options.client) {
            url += `&client=${this.options.client}`;
        }
        // Add where for any relevant filters
        const relevantFilters = lodash_1.default.where(filters, { table: design.table });
        // If any, create and
        const whereClauses = lodash_1.default.map(relevantFilters, (f) => (0, expressions_1.injectTableAlias)(f.jsonql, "main"));
        // Wrap if multiple
        if (whereClauses.length > 1) {
            where = { type: "op", op: "and", exprs: whereClauses };
        }
        else {
            where = whereClauses[0];
        }
        if (where) {
            url += "&where=" + encodeURIComponent((0, compressJson_1.default)(where));
        }
        return url;
    }
    /** Gets hover over data for hover over items
     * @param design The design of the layer
     * @param data The data of the current item being hovered over. e.g. { id: 123 }
     * @param filters The filters to apply to the hover over data, not including filtering down to the current item
     * @returns A promise that resolves to the hover over data, indexed by the id of the hover over item
     */
    getHoverOverData(design, data, filters) {
        // Create layer
        const layer = LayerFactory_1.default.createLayer(this.options.layerView.type);
        return layer.getHoverOverData({
            design,
            data,
            filters,
            schema: this.options.schema,
            dataSource: this.options.dataSource,
        });
    }
}
